import { exportDataToCsv } from "@intrasync/utils";

export interface ColumnConfig {
  key: string;
  label: string;
  format?: "text" | "currency" | "percent" | "date" | "number";
  width?: number;
}

export interface MultiSheetData<T = Record<string, unknown>> {
  sheetName: string;
  data: T[];
}

export interface PdfSectionData<T = Record<string, unknown>> {
  sectionTitle: string;
  data: T[];
}

interface CsvExportOptions {
  fileName: string;
  includeHeaders?: boolean;
}

interface ExcelExportOptions {
  fileName: string;
  sheetName?: string;
  title?: string;
  includeHeaders?: boolean;
  includeSummary?: boolean;
}

interface PdfExportOptions {
  fileName: string;
  title?: string;
  subtitle?: string;
  includeHeaders?: boolean;
  includeSummary?: boolean;
}

function pickColumns(
  data: Record<string, unknown>[],
  columns: ColumnConfig[]
): Record<string, unknown>[] {
  return data.map((row) => {
    const picked: Record<string, unknown> = {};
    for (const col of columns) {
      picked[col.label] = row[col.key] ?? "";
    }
    return picked;
  });
}

export function exportToCsv(
  data: Record<string, unknown>[],
  columns: ColumnConfig[],
  options: CsvExportOptions
): void {
  const mapped = pickColumns(data, columns);
  exportDataToCsv(options.fileName, mapped);
}

export async function exportToExcel(
  data: Record<string, unknown>[],
  columns: ColumnConfig[],
  options: ExcelExportOptions
): Promise<void> {
  // Fall back to CSV export since xlsx library is not available
  const mapped = pickColumns(data, columns);
  exportDataToCsv(`${options.fileName}.xlsx`, mapped);
}

export async function exportToExcelMultiSheet<
  T extends Record<string, unknown>,
>(
  sheets: MultiSheetData<T>[],
  columns: ColumnConfig[],
  options: ExcelExportOptions
): Promise<void> {
  // Fall back to CSV export of combined sheets
  const allData: Record<string, unknown>[] = [];
  for (const sheet of sheets) {
    allData.push(...pickColumns(sheet.data as Record<string, unknown>[], columns));
  }
  exportDataToCsv(`${options.fileName}.xlsx`, allData);
}

export function exportToPdf(
  data: Record<string, unknown>[],
  columns: ColumnConfig[],
  options: PdfExportOptions
): void {
  // Fall back to CSV export since PDF library is not available
  const mapped = pickColumns(data, columns);
  exportDataToCsv(`${options.fileName}.pdf`, mapped);
}

export function exportToPdfMultiSection<T extends Record<string, unknown>>(
  sections: PdfSectionData<T>[],
  columns: ColumnConfig[],
  options: PdfExportOptions
): void {
  // Fall back to CSV export of combined sections
  const allData: Record<string, unknown>[] = [];
  for (const section of sections) {
    allData.push(...pickColumns(section.data as Record<string, unknown>[], columns));
  }
  exportDataToCsv(`${options.fileName}.pdf`, allData);
}
